import {
  SUB_TEMPLATES,
  BACKTRACK_TEMPLATES,
  DIALOG,
} from '../../containers/OrderHelpTemplateContainer/templateTypes';

import { createSelfHelpQueries, getSupportedDeepLink } from '../../utils/deepLinkUri';
import getSafe from '../../utils/getSafe';

export const createProcessingSteps = (processingSteps) => {
  const steps = [];
  if (processingSteps) {
    processingSteps.forEach((step) =>
      steps.push({
        title: step.title,
        endsAtProgressPercentage: step.ends_at_progress_percentage,
        isCurrent: step.is_current,
      }),
    );
  }
  return steps;
};

export const createStatusButtonFromDeepLinkUri = (messageTarget) => {
  if (!messageTarget) {
    return null;
  }

  const uriQueries = createSelfHelpQueries(messageTarget.uri);

  if (!uriQueries) {
    return null;
  }

  return {
    text: messageTarget.text,
    uriQueries,
  };
};

export const createStatus = (status) => ({
  type: status.type,
  id: status.id,
  isSharedOrder: status.attributes.authenticated_via === 'sharing_token',
  attributes: {
    title: status.attributes.title,
    etaMessage: status.attributes.eta_message,
    message: status.attributes.message,
    progress: status.attributes.progress_animation
      ? {
          animationType: status.attributes.progress_animation.type,
          startAtPercentage: status.attributes.progress_animation.start_at_percentage,
          finishAtPercentage: status.attributes.progress_animation.finish_at_percentage,
          duration: status.attributes.progress_animation.duration_ms,
        }
      : null,
    uiStatus: status.attributes.ui_status,
    processingSteps: createProcessingSteps(status.attributes.processing_steps),
    showLiveIndicator: status.attributes.show_live_indicator,
    showShareOrderTrackerLink: status.attributes.show_share_order_tracker_link,
    canShowRateApp: status.attributes.can_show_rate_app,
    formattedUpdatedAt: status.attributes.updated_at_formatted,
    updatedAt: status.attributes.updated_at,
    analytics: status.attributes.analytics,
    currentProgressPercentage: status.attributes.current_progress_percentage,
    isBerryTheme: status.attributes.colour_scheme === 'berry',
    advisory: status.attributes.advisory,
    emphasizeAdvisory: status.attributes.emphasize_advisory,
    statusAnimationUrl: status.attributes.status_animation_url,
    statusButton: createStatusButtonFromDeepLinkUri(status.attributes.message_target),
    supplementaryMessage: status.attributes.supplementary_message,
  },
});

export const getShouldShowMap = (status, locations, rider) => {
  const locationsTypes = (locations && locations.map((location) => location.attributes.type)) || [];
  const hasDeliveryLocations =
    locationsTypes.includes('RIDER') && locationsTypes.includes('CUSTOMER');
  const hasCollectionLocations = locationsTypes.includes('RESTAURANT');
  const hasRider = getSafe(() => rider.attributes.vehicle_type);
  const showMap = getSafe(() => status.attributes.show_map);
  const isFailedState = getSafe(() => status.attributes.ui_status) === 'FAILED';

  const showDeliveryMap = (hasDeliveryLocations && hasRider && showMap && !isFailedState) || false;
  const showCollectionsMap = (hasCollectionLocations && showMap && !isFailedState) || false;

  return showDeliveryMap || showCollectionsMap;
};

export const createOrderItems = (orderItems) => {
  const items = [];
  if (orderItems) {
    orderItems.forEach((orderItem) =>
      items.push({
        name: orderItem.name,
        modifiersDescription: orderItem.modifiers_description,
        quantity: orderItem.quantity,
        totalPrice: orderItem.total_price,
      }),
    );
  }
  return items;
};

export const getTotalAmount = (totals) => {
  const totalData = totals && totals.find((total) => total.type === 'total');
  return totalData && totalData.amount;
};

export const createOrder = (order) => ({
  id: order.id,
  orderDetails: {
    orderItems: createOrderItems(order.attributes.full_items),
    orderNumber: order.attributes.order_number,
  },
  totalAmount: getTotalAmount(order.attributes.totals),
  deliveryAddress: order.attributes.address,
  deliveryNote: order.attributes.delivery_note,
  totals: order.attributes.totals,
  placedAtMessage: order.attributes.placed_at_message,
  restaurantName: order.attributes.restaurant_name,
  imageUrl: order.attributes.image_url,
  country: order.attributes.country,
  description: order.attributes.description,
  itemsShortDescription: order.attributes.items,
  sharingToken: order.attributes.sharing_token,
  sharingShortUrl: order.attributes.sharing_short_url,
  sharingEnabled: order.attributes.sharing_enabled || false,
  orderOwnerName: order.attributes.customer_name,
});

export const createLocation = (location) => {
  const {
    id,
    attributes: { latitude: lat, longitude: lng, type },
  } = location;

  return {
    id,
    position: {
      lat,
      lng,
    },
    type,
  };
};

export const createRider = ({ name, subtitle, vehicle_type: vehicleType }) => ({
  name,
  subtitle,
  vehicleType: vehicleType ? `RIDER_${vehicleType}` : 'RIDER_DEFAULT',
});

const infoBannerHasValidProperties = (props) => {
  const validProperties = {
    colour_scheme: ['white', 'teal', 'berry'],
    modal_type: ['whatsapp'],
    image_id: ['moto_rider', 'phone', 'download_app'],
  };
  const bannerProperties = { ...props, ...props.target };

  let valid = true;
  Object.keys(validProperties).forEach((propertyKey) => {
    if (
      bannerProperties[propertyKey] &&
      !validProperties[propertyKey].includes(bannerProperties[propertyKey])
    ) {
      valid = false;
    }
  });

  return valid;
};

const infoBannerBadges = {
  moto_rider: 'rider',
  phone: 'alerts',
  download_app: 'download',
};

export const createInfoBanner = (infoBanner) => {
  if (infoBannerHasValidProperties(infoBanner.attributes)) {
    const theme = infoBanner.attributes.colour_scheme;
    const type = theme === 'white' ? 'SERVICE' : 'PROMO';

    const target = infoBanner.attributes.target;
    const targetUrl = target && target.url;
    const deepLinkAction = targetUrl && getSupportedDeepLink(targetUrl);
    return {
      id: infoBanner.id,
      type,
      title: infoBanner.attributes.title,
      body: infoBanner.attributes.message,
      badge: infoBannerBadges[infoBanner.attributes.image_id],
      theme,
      href: deepLinkAction ? null : targetUrl,
      deepLinkUri: deepLinkAction ? targetUrl : null,
      modalType: target && target.modal_type,
      isModalOpen: false,
    };
  }

  return null;
};

export const createPunchCard = (punchCard) => {
  const totalStamps = punchCard.total_stamps;
  const currentStamps = punchCard.confirmed_stamps + punchCard.pending_stamps;
  const shouldShow = currentStamps !== 0 && currentStamps <= totalStamps;

  return {
    shouldShow,
    title: punchCard.card_title,
    body: punchCard.card_description,
    currentStamps,
    totalStamps,
  };
};

export const createContactDetails = (contactDetails) => ({
  showContactButton: ['contact_rider', 'contact_rider_unavailable'].includes(contactDetails.id),
  canContact: contactDetails.attributes.type === 'call_number',
  text: contactDetails.attributes.text,
});

export const createContactModal = (contact) => ({
  header: contact.title,
  text: contact.header,
  riderNumber: contact.data && contact.data.number,
});

export const createImpactRadiusTracking = (impactRadius) => {
  if (impactRadius && impactRadius.item) {
    return {
      item: impactRadius.item,
      promoCode: impactRadius.promo_code,
      currency: impactRadius.currency,
    };
  }
  return null;
};

export const createShopBackTracking = (shopback) => {
  if (shopback) {
    return {
      amount: shopback.amount,
      customerType: shopback.customer_type,
      deliveredAt: shopback.delivered_at,
      orderId: shopback.order_id,
      restaurantId: shopback.restaurant_id,
    };
  }

  return null;
};

export const createTracking = (tracking) => ({
  impactRadius: createImpactRadiusTracking(tracking.impact_radius),
  shopBack: createShopBackTracking(tracking.shopback),
  facebookPurchaseTracking: tracking.facebook_purchase_tracking,
});

export const createInteractionOrderItems = (orderItems) => {
  if (orderItems) {
    return orderItems.map((item, itemIndex) => ({
      id: `${item.order_item_id}-${itemIndex}`,
      orderItemId: item.order_item_id,
      name: item.name,
      modifiers:
        item.modifiers &&
        item.modifiers.map((modifier, modIndex) => ({
          id: `${item.order_item_id}-${itemIndex}-${modifier.order_item_id}-${modIndex}`,
          name: modifier.name,
          orderItemId: modifier.order_item_id,
        })),
    }));
  }

  return null;
};

export const createInteractionResolutions = (resolutions) => {
  if (resolutions) {
    return resolutions.map((resolution) => ({
      id: resolution.id,
      issueId: resolution.issue_id,
      selected: resolution.selected,
      price: resolution.data.amount !== null ? resolution.data.formatted_price : null,
      title: resolution.data.title,
      subtitle: resolution.data.subtitle,
      footer: resolution.data.footer,
      cta: resolution.data.call_to_action,
      confirmation: {
        title: resolution.data.confirmation_title,
        message: resolution.data.confirmation_message,
      },
    }));
  }

  return null;
};

export const createInteractionFeedbackScore = (feedbackScores) => {
  if (feedbackScores) {
    return feedbackScores.map((score) => ({
      id: score.id,
    }));
  }

  return null;
};

export const createInteractionTemplate = (oldTemplateId, templateId) => {
  let templateTypes = {};

  if (SUB_TEMPLATES.includes(templateId)) {
    templateTypes = {
      subType: templateId,
    };
  } else {
    templateTypes = {
      type: templateId,
      subType: null,
    };
  }

  return Object.assign({ type: oldTemplateId }, templateTypes);
};

export const createInteractionActions = (oldActions, actions, hasSubType) => {
  let templateActions = actions;

  if (!actions && hasSubType) {
    templateActions = oldActions;
  }

  return templateActions;
};

export const createInteractionButtonDetails = (data) => ({
  buttonText: data.button && data.button.text ? data.button.text : data.button_text,
  buttonType: data.button && data.button.type,
});

export const getShouldShowPreviousButton = (previousState, currentTemplateType) => {
  const {
    type: previousTemplateType,
    isHelpHome: previousPageIsHelpHome,
    isSpecificHelpPage,
  } = previousState;

  const isFromAndToBacktrackTemplateTypes =
    BACKTRACK_TEMPLATES.includes(previousTemplateType) &&
    BACKTRACK_TEMPLATES.includes(currentTemplateType);

  return (
    (previousPageIsHelpHome && !isSpecificHelpPage && isFromAndToBacktrackTemplateTypes) || false
  );
};

export const createInteractionTemplateData = (oldState, templateData) => {
  const {
    id: actionId,
    type: templateId,
    title,
    header,
    data: {
      actions,
      order_items: orderItems,
      resolutions,
      scores: feedbackScores,
      scores_header: feedbackScoresHeader,
      email,
      number,
      text,
      submit_text: submitText,
      placeholder_text: placeholderText,
      update_interaction_on_close: updateInteractionOnClose,
      // contact_reason: liveChatContactReason,
      department: liveChatDepartment,
      title: alternativeTitle,
      message,
    },
  } = templateData;

  const templates = createInteractionTemplate(oldState.type, templateId);

  const formattedOrderItems = createInteractionOrderItems(orderItems);

  const formattedResolutions = createInteractionResolutions(resolutions);

  const formattedFeedbackScore = createInteractionFeedbackScore(feedbackScores);

  const templateActions = createInteractionActions(
    oldState.data && oldState.data.actions,
    actions,
    !!templates.subType,
  );

  const isDialogSubType = templates.subType === DIALOG;
  const isHelpHome = actionId === 'order_help';
  const showSelfHelpPreviousButton = getShouldShowPreviousButton(oldState, templateId);

  return {
    isHelpHome,
    isFirstHelpPage: !oldState.actionId,
    showSelfHelpPreviousButton,
    actionId,
    title: isDialogSubType ? oldState.title : title,
    header,
    showDialog: isDialogSubType,
    data: {
      actions: templateActions,
      orderItems: formattedOrderItems,
      resolutions: formattedResolutions,
      feedbackScores: formattedFeedbackScore,
      feedbackScoresHeader,
      email,
      number,
      text: isDialogSubType ? oldState.text : text,
      submitText,
      placeholderText,
      dialogTitle: isDialogSubType ? title : '',
      dialogText: isDialogSubType ? text : '',
      updateInteractionOnClose,
      liveChat: {
        // contactReason: liveChatContactReason, // TODO - removing it for now so that we can track a bug
        department: liveChatDepartment,
      },
      title: alternativeTitle,
      message,
      ...createInteractionButtonDetails(templateData.data),
    },
    ...templates,
  };
};

export const createOrderHistory = (ordersList) =>
  ordersList.map((order) => {
    // Ideally this should come back from the API
    const statusTexts = {
      NEW: 'Submitted',
      ACCEPTED: 'Accepted',
      CONFIRMED: 'Confirmed',
      ALMOST_READY: 'Almost Ready',
      READY: 'Ready',
      IN_TRANSIT: 'In Transit',
      DELIVERED: 'Delivered',
      PICKED_UP_BY_CUSTOMER: 'Collected',
      COULD_NOT_DELIVER: 'Could Not Deliver',
      REJECTED: 'Rejected',
      CANCELED: 'Canceled',
      FAILED: 'Failed',
    };

    const completedStatuses = [
      'DELIVERED',
      'PICKED_UP_BY_CUSTOMER',
      'COULD_NOT_DELIVER',
      'REJECTED',
      'CANCELED',
      'FAILED',
    ];

    const failedStatuses = ['COULD_NOT_DELIVER', 'REJECTED', 'CANCELED', 'FAILED'];

    const orderId = `${order.id}`;
    const orderStatus = order.status;
    let orderUrl = `/orders/${orderId}/status`;
    if (completedStatuses.includes(orderStatus)) {
      orderUrl = `/orders/${orderId}`;
    }

    return {
      id: orderId,
      statusText: statusTexts[orderStatus],
      shouldHighlightStatusFailed: failedStatuses.includes(orderStatus),
      formattedDate: order.formatted_delivered_date,
      // TODO - remove once we get formattedDate from API
      legacySubmittedDate: order.submitted_at,
      // TODO - remove once we get formattedCost from API
      currencyCode: order.currency_code,
      currency: order.currency_symbol,
      total: order.total,
      restaurantId: order.restaurant && order.restaurant.id,
      restaurantName: order.restaurant && order.restaurant.name,
      restaurantImage: order.restaurant && order.restaurant.image_url,
      orderUrl,
    };
  });

export const createOrderReceipt = (order) => ({
  restaurantName: order.restaurant && order.restaurant.name,
});

export const createInteractionHistory = (interactionData = {}) => {
  if (!BACKTRACK_TEMPLATES.includes(interactionData.type)) {
    return {};
  }

  return {
    [interactionData.actionId]: interactionData,
  };
};

export const createMissedCallModal = (missedCallModalAttributes) => ({
  actionButtonText: missedCallModalAttributes.action_button_text,
  callTimestamp: missedCallModalAttributes.call_timestamp,
  canCallRider: missedCallModalAttributes.can_call_rider,
  riderNumber: missedCallModalAttributes.phone_number,
  text: missedCallModalAttributes.text,
  title: missedCallModalAttributes.title,
  parentCallSid: missedCallModalAttributes.parent_call_sid,
});

export const createOrderConfirmationReference = (createOrderConfirmationReferenceAttributes) => ({
  title: createOrderConfirmationReferenceAttributes.title,
  subtitle: createOrderConfirmationReferenceAttributes.subtitle,
  shortOrderId: createOrderConfirmationReferenceAttributes.order_id_short,
});

export const createDirectionHelpAction = (directionHelpActionAttributes) => ({
  restaurantName: directionHelpActionAttributes.restaurant_name,
  address: directionHelpActionAttributes.address,
  latitude: directionHelpActionAttributes.latitude,
  longitude: directionHelpActionAttributes.longitude,
  modalTitle: directionHelpActionAttributes.modal_title,
});

export const createOrderStatusFailedAttemptsCount = (statusCode, currentFailedAttempts) => {
  if (statusCode === 200) {
    return 0;
  }

  const isLoggableFailedAttemptStatus =
    (statusCode >= 400 || statusCode >= 500) && ![599].includes(statusCode);

  if (isLoggableFailedAttemptStatus) {
    return currentFailedAttempts + 1;
  }

  return currentFailedAttempts;
};
